import { useEffect, useState } from 'react'
import {
  ASSESSMENT_RECIPIENTS_URL,
  ASSESSMENT_RECIPIENT_STATUSES
} from 'services/constants'
import { getRequest, patchRequest } from 'utils/api'
import {
  CardContent,
  Table,
  TableBody,
  TableContainer,
  Typography,
  TableCell,
  TableRow,
  CardHeader
} from '@mui/material'
import InCardStack from 'components/atoms/container/InCardStack'
import ProfileAvatar from 'components/atoms/ProfileAvatar'
import { getGoalInfoById } from 'utils/getSupportingInfo'
import { useNavigate } from 'react-router-dom'
import { ASSESSMENTS, ASSESSMENT_OTHERS } from 'routes/constants'
import { formatDayMonth } from 'utils/formatDateTime'
import RequestRetryAlert from 'components/atoms/RequestRetryAlert'
import LeftJustifyCardContent from 'components/atoms/container/LeftJustifyCardContent'
import { NotificationBodyText } from 'components/atoms/Text'
import { getFullName } from 'utils/formatText'
import { useTranslation } from 'react-i18next'
import colors from 'styles/colors'
import AcceptRejectButtons from 'components/atoms/button/AcceptRejectButtons'
import CommonDialog from 'components/molecules/notificationOverlay/CommonDialog'
import OverlayLoading from 'components/atoms/OverlayLoading'
import FeedbackItemContainer from 'components/atoms/container/FeedbackItemContainer'
import BlankEventItem from 'components/atoms/BlankEventItem'
import ActivityListContainer from 'components/molecules/home/ActivityListContainer'
import MarkChatUnreadOutlinedIcon from '@mui/icons-material/MarkChatUnreadOutlined'
import moment from 'moment'

const backgroundColor = colors.MetalGrey50

const AssessmentsList = ({ displayCount = 3, updatedStyle = false }) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [requestError, setRequestError] = useState(undefined)
  const [assemssmentInvites, setAssessmentInvites] = useState([])
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedAssessment, setSelectedAssessment] = useState(undefined)
  const [loading, setLoading] = useState(false)

  const fetchData = async () => {
    const res = await getRequest(ASSESSMENT_RECIPIENTS_URL)
    if (res?.status === 200) {
      const invites = await Promise.all(
        res.data.data.map(async invite => {
          const goal = await getGoalInfoById(invite.assessmentRequest.goalId)
          const updated = invite
          updated.goalName = `${goal.leadershipModelName} - ${goal.name}`
          return updated
        })
      )
      setAssessmentInvites(invites)
      setRequestError(false)
    } else {
      setRequestError(true)
    }
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const rejectAssessment = invite => {
    setSelectedAssessment(invite)
    setDialogOpen(true)
  }

  const onRejectDialogClose = async reject => {
    if (reject) {
      setLoading(true)
      const res = await patchRequest(
        `${ASSESSMENT_RECIPIENTS_URL}/${selectedAssessment.id}`,
        { status: ASSESSMENT_RECIPIENT_STATUSES.REJECTED }
      )
      if (res.status === 200) {
        await fetchData()
      } else {
        setRequestError(true)
      }
      setLoading(false)
    }
    setDialogOpen(false)
  }

  const InviteList = () => (
    <>
      {assemssmentInvites.slice(0, displayCount).map(invite => (
        <FeedbackItemContainer
          key={`assessment-${invite.id}`}
          backgroundColor={backgroundColor}
          updatedStyle={updatedStyle}
          subHeader={t('home.expires', {
            date: formatDayMonth(
              moment(invite.assessmentRequest.sendAt).add(14, 'days'),
              i18n.resolvedLanguage
            )
          })}
          user={invite.assessmentRequest?.user}
          acceptLabel={t('feedbackArea.giveFeedback')}
          acceptAction={() =>
            navigate(`${ASSESSMENT_OTHERS}/${invite.assessmentRequestId}`, {
              state: {
                senderName: getFullName(invite),
                goalId: invite.assessmentRequest?.goalId,
                sender: invite.assessmentRequest?.userId
              }
            })
          }
          rejectAction={() => rejectAssessment(invite)}
          acceptButtonProps={{ color: 'muted' }}
        />
      ))}
    </>
  )
  return (
    <>
      {updatedStyle ? (
        <ActivityListContainer
          title={t('feedbackArea._360Requests')}
          icon={<MarkChatUnreadOutlinedIcon />}
          itemCount={assemssmentInvites.length}
          listTitle={t('home.showingCount', {
            count:
              assemssmentInvites.length > displayCount
                ? displayCount
                : assemssmentInvites.length,
            total: assemssmentInvites.length
          })}
          viewAllAction={() => navigate(ASSESSMENTS)}>
          {assemssmentInvites.length > 0 ? (
            <InviteList />
          ) : (
            <BlankEventItem
              showConfirm={true}
              showReject={true}
              description={t('assessments.noOpenFeedback')}
            />
          )}
          {requestError === true && <RequestRetryAlert />}
        </ActivityListContainer>
      ) : (
        <>
          {assemssmentInvites.length > 0 ? (
            <>
              <CardContent>
                <InCardStack>
                  <Typography variant='h6' color='textSecondary' align='center'>
                    {t('assessments.invitedToProvideRating.part1')}
                  </Typography>
                  <Typography variant='h6' color='textSecondary' align='center'>
                    {t('assessments.invitedToProvideRating.part2')}
                  </Typography>
                </InCardStack>
              </CardContent>
              <CardContent>
                <TableContainer>
                  <Table>
                    <TableBody>
                      {assemssmentInvites.map(invite => (
                        <TableRow
                          key={invite.id}
                          sx={{ borderBottomWidth: '1px' }}>
                          <TableCell>
                            <CardHeader
                              avatar={
                                <ProfileAvatar
                                  user={invite.assessmentRequest?.user}
                                />
                              }
                              title={getFullName(
                                invite.assessmentRequest?.user
                              )}
                              sx={{ padding: 0 }}
                            />
                          </TableCell>
                          <TableCell>{invite.goalName}</TableCell>
                          <TableCell>
                            <AcceptRejectButtons
                              acceptLabel={t('feedbackArea.giveFeedback')}
                              onAccept={() =>
                                navigate(
                                  `${ASSESSMENT_OTHERS}/${invite.assessmentRequestId}`,
                                  {
                                    state: {
                                      senderName: getFullName(invite),
                                      goalId: invite.assessmentRequest?.goalId,
                                      sender: invite.assessmentRequest?.userId
                                    }
                                  }
                                )
                              }
                              onReject={() => rejectAssessment(invite)}
                              background={colors.White}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </>
          ) : (
            <LeftJustifyCardContent>
              <NotificationBodyText fontSize={20}>
                {t('assessments.noOutstandingRating')}
              </NotificationBodyText>
            </LeftJustifyCardContent>
          )}
          {requestError === true ? (
            <CardContent>
              <RequestRetryAlert />
            </CardContent>
          ) : null}
        </>
      )}
      <CommonDialog
        open={dialogOpen}
        onClose={onRejectDialogClose}
        titleText={t('feedbackArea._360Requests')}
        notificationText={t('feedbackArea.dismissRequestMessage', {
          firstName: getFullName(selectedAssessment)
        })}
        confirmText={t('common.dismiss')}
        cancelText={t('common.cancel')}
      />
      <OverlayLoading open={loading} />
    </>
  )
}

export default AssessmentsList
